import { useRef, useState, type DragEvent, type KeyboardEvent } from 'react';
import { Ellipsis } from 'lucide-react';
import type { ContactBoardColumn } from '@brimo/shared';
import { clsx } from '../../ds/clsx';
import { Icon, MenuItem, MenuList, Popover, Select } from '../../ds/components';
import type { BoardCard, BoardColumn } from './types';
import styles from './ContactsPage.module.css';

export interface ContactsBoardProps {
  columns: BoardColumn[];
  mobile: boolean;
  onOpen: (id: string) => void;
  /** A card dropped on any column, or "Lifecycle ändern" from the card menu (touch fallback). */
  onMove: (card: BoardCard, from: ContactBoardColumn, to: ContactBoardColumn | null) => void;
  /** "+ N weitere" → the list filtered by the column. */
  onMore: (column: ContactBoardColumn) => void;
}

const DRAG_TYPE = 'application/x-brimo-contact';

function CardMenu({ onChange }: { onChange: () => void }) {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLButtonElement>(null);
  return (
    <>
      <button
        ref={ref}
        type="button"
        className={styles.cardMenu}
        aria-label="Kartenmenü"
        aria-haspopup="menu"
        aria-expanded={open}
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <Icon icon={Ellipsis} size={16} />
      </button>
      <Popover open={open} onClose={() => setOpen(false)} anchorRef={ref} width={200} initialFocus="none">
        <MenuList label="Karte" density="compact" onClose={() => setOpen(false)}>
          <MenuItem
            label="Lifecycle ändern"
            onSelect={() => {
              setOpen(false);
              onChange();
            }}
          />
        </MenuList>
      </Popover>
    </>
  );
}

/** Kontakte 03 · the lifecycle board: seven columns, draggable cards, "+ N weitere". */
export function ContactsBoard({ columns, mobile, onOpen, onMove, onMore }: ContactsBoardProps) {
  const [over, setOver] = useState<ContactBoardColumn | null>(null);
  const [dragging, setDragging] = useState<string | null>(null);
  const [mobileColumn, setMobileColumn] = useState<ContactBoardColumn>(columns[0]?.key ?? 'new');

  function findCard(id: string): { card: BoardCard; column: ContactBoardColumn } | null {
    for (const column of columns) {
      const card = column.cards.find((entry) => entry.id === id);
      if (card) return { card, column: column.key };
    }
    return null;
  }

  function onDragStart(event: DragEvent<HTMLElement>, card: BoardCard) {
    event.dataTransfer.setData(DRAG_TYPE, card.id);
    event.dataTransfer.effectAllowed = 'move';
    setDragging(card.id);
  }

  function onDrop(event: DragEvent<HTMLElement>, column: ContactBoardColumn) {
    event.preventDefault();
    setOver(null);
    setDragging(null);
    const id = event.dataTransfer.getData(DRAG_TYPE);
    const found = id ? findCard(id) : null;
    if (!found || found.column === column) return;
    onMove(found.card, found.column, column);
  }

  function onCardKeyDown(event: KeyboardEvent<HTMLElement>, id: string) {
    if (event.target !== event.currentTarget) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      onOpen(id);
    }
  }

  const visible = mobile ? columns.filter((column) => column.key === mobileColumn) : columns;

  return (
    <div>
      {mobile ? (
        <div className={styles.boardSelect}>
          <Select
            label="Spalte"
            labelHidden
            value={mobileColumn}
            options={columns.map((column) => ({
              value: column.key,
              label: `${column.label} · ${column.count}`,
            }))}
            onChange={(event) => setMobileColumn(event.target.value as ContactBoardColumn)}
          />
        </div>
      ) : null}
      <div className={styles.board} role="list" aria-label="Lifecycle-Board">
        {visible.map((column) => (
          <div
            key={column.key}
            role="listitem"
            aria-label={`${column.label} ${column.count}`}
            className={clsx(
              styles.column,
              column.muted && styles.columnMuted,
              over === column.key && styles.columnOver,
            )}
            onDragOver={(event) => {
              if (!event.dataTransfer.types.includes(DRAG_TYPE)) return;
              event.preventDefault();
              event.dataTransfer.dropEffect = 'move';
              if (over !== column.key) setOver(column.key);
            }}
            onDragLeave={(event) => {
              if (event.currentTarget.contains(event.relatedTarget as Node | null)) return;
              if (over === column.key) setOver(null);
            }}
            onDrop={(event) => onDrop(event, column.key)}
          >
            <div className={styles.columnHead}>
              <span className={styles.columnLabel}>{column.label}</span>
              <span className={styles.columnCount}>{column.count}</span>
            </div>
            {column.cards.map((card) => (
              <div
                key={card.id}
                role="button"
                tabIndex={0}
                draggable={!mobile}
                className={clsx(styles.card, dragging === card.id && styles.cardDragging)}
                onClick={() => onOpen(card.id)}
                onKeyDown={(event) => onCardKeyDown(event, card.id)}
                onDragStart={(event) => onDragStart(event, card)}
                onDragEnd={() => {
                  setDragging(null);
                  setOver(null);
                }}
              >
                <div className={styles.cardName}>{card.name}</div>
                <div className={styles.cardMeta}>
                  <span className={styles.cardMetaText}>{card.meta ?? ''}</span>
                  {card.value ? (
                    <span
                      className={clsx(
                        styles.cardValue,
                        (card.overdue || column.key === 'new') && styles.cardValueUrgent,
                      )}
                    >
                      {card.value}
                    </span>
                  ) : null}
                </div>
                <CardMenu onChange={() => onMove(card, column.key, null)} />
              </div>
            ))}
            {column.cards.length === 0 ? <span className={styles.columnEmpty}>Keine Kontakte.</span> : null}
            {column.more > 0 ? (
              <button type="button" className={styles.columnMore} onClick={() => onMore(column.key)}>
                + {column.more} weitere
              </button>
            ) : null}
          </div>
        ))}
      </div>
    </div>
  );
}
